import React, { useEffect, useState } from 'react';

import { DatePicker, Input } from '../../form';
import RevealButton from '../../Reveal-button/Reveal-button';
import { AssetAdvancedSettings } from '../AssetAdvancedSettings/AssetAdvancedSettings';

import { useTranslation } from 'react-i18next';
import { CustomAssetFormProps } from './types';
import { CustomNumber } from '../../CustomNumber/CustomNumber';
import { getPrecision, numberFormat } from '../../../helpers/calc';
import { NumberFormatValues } from 'react-number-format';
import { useQuery } from '@apollo/client';
import { AssetPrice } from '../FinancialAsset';
import { GetAssetValueVars } from '../types';
import { GET_ASSET_VALUE } from '../../../http/apollo-queries';
import { formatAWSDate } from '../../../helpers/functions';
import { CurrencyCodeEnum } from '../../../constants/enums';
import { logger } from '../../../helpers/logger';

export const CustomAssetForm = ({
  action,
  reportingCurrency,
  formik,
}: CustomAssetFormProps) => {
  const { t } = useTranslation();
  const [isMoreRevealed, setIsMoreRevealed] = useState(false);

  // HANDLERS
  const handleEntryPriceInAssetCurrencyChange = ({
    floatValue,
  }: NumberFormatValues) => {
    formik.setFieldValue('entryPriceInAssetCurrency', floatValue);
    formik.setFieldValue('entryPriceInReportingCurrency', floatValue);
  };

  const handleCurrentPriceInAssetCurrencyChange = ({
    floatValue,
  }: NumberFormatValues) => {
    formik.setFieldValue('currentPriceInAssetCurrency', floatValue);
    formik.setFieldValue('currentPriceInReportingCurrency', floatValue);
  };

  const handlePurchasedAtChange = (date: Date) => {
    formik.setFieldValue('purchasedAt', formatAWSDate(date));
  };

  // EFFECTS
  // Reset form data on load
  useEffect(() => {
    if (action === 'CREATE') {
      formik.setValues({
        ...formik.values,
        purchasedAt: formatAWSDate(new Date()),
        assetData: undefined,
        quantity: undefined,
        entryPriceInReportingCurrency: undefined,
        entryPriceInAssetCurrency: undefined,
        currentPriceInAssetCurrency: undefined,
        currentPriceInReportingCurrency: undefined,
        entryExchangePrice: 1,
      });
    }
  }, [action]);

  return (
    <>
      <div className="flex w-full px-20">
        <Input
          key="assetName"
          size="full"
          name="assetName"
          id="assetName"
          formik={formik}
          type="text"
          placeholder={t(
            'strategies.configureAsset.form.placeholder.assetName'
          )}
          label={t('strategies.configureAsset.form.label.assetName')}
          validate={(value: string) => {
            return value.length === 0;
          }}
        />
      </div>
      <div className="flex w-full gap-8 mt-10 px-20">
        {/* QTY */}
        <div className={'w-full'}>
          <CustomNumber
            allowNegative={false}
            decimalScale={getPrecision(formik.values.quantity || 0)}
            decimalScaleOnFocus={getPrecision(formik.values.quantity || 0)}
            placeholder={t('strategies.configureAsset.form.label.quantity')}
            readOnly={false}
            id={'quantity'}
            suffixOverlay={true}
            value={formik.values.quantity}
            onValueChange={({ floatValue }: NumberFormatValues) =>
              formik.setFieldValue('quantity', floatValue)
            }
            showLabel={true}
            fixedLabel={false}
            label={t('strategies.configureAsset.form.placeholder.quantity')}
            validate={(value) => !!value && value > 0}
            // loading={loadingExchangePrice}
          />
        </div>
        {/* CURRENT PRICE */}
        <div className={'w-full'}>
          <CustomNumber
            allowNegative={false}
            decimalScale={getPrecision(
              formik.values.currentPriceInAssetCurrency || 0
            )}
            decimalScaleOnFocus={getPrecision(
              formik.values.currentPriceInAssetCurrency || 0
            )}
            placeholder={t(
              'strategies.custom.form.placeholder.currentPriceInAssetCurrency'
            )}
            prefix={undefined}
            readOnly={false}
            id={'currentPriceInAssetCurrency'}
            suffix={formik.values.assetData?.currency || reportingCurrency}
            suffixOverlay={true}
            value={formik.values.currentPriceInAssetCurrency}
            onValueChange={handleCurrentPriceInAssetCurrencyChange}
            showLabel={true}
            fixedLabel={false}
            label={t(
              'strategies.custom.form.label.currentPriceInAssetCurrency'
            )}
            validate={(value) => !!value && value > 0}
          />

          {formik.values.currentPriceInReportingCurrency !== undefined && (
            <div className={'text-xs text-grey-50'}>
              {t('strategies.configureAsset.inReportingCurrency', {
                value: numberFormat(
                  formik.values.currentPriceInReportingCurrency
                ),
                currency: reportingCurrency,
              })}
            </div>
          )}
        </div>
      </div>

      <div className="flex w-full gap-8 mt-10 px-20">
        {/* ENTRY PRICE */}
        <div className={'w-full'}>
          <CustomNumber
            allowNegative={false}
            decimalScale={getPrecision(
              formik.values.entryPriceInAssetCurrency || 0
            )}
            decimalScaleOnFocus={getPrecision(
              formik.values.entryPriceInAssetCurrency || 0
            )}
            placeholder={t(
              'strategies.custom.form.label.entryPriceInAssetCurrency'
            )}
            prefix={undefined}
            readOnly={false}
            id={'entryPriceInAssetCurrency'}
            suffix={formik.values.assetData?.currency || reportingCurrency}
            suffixOverlay={true}
            value={formik.values.entryPriceInAssetCurrency}
            onValueChange={handleEntryPriceInAssetCurrencyChange}
            showLabel={true}
            fixedLabel={false}
            label={t(
              'strategies.custom.form.placeholder.entryPriceInAssetCurrency'
            )}
            validate={(value) => !!value && value > 0}
          />

          {formik.values.entryPriceInReportingCurrency !== undefined && (
            <div className={'text-xs text-grey-50'}>
              {t('strategies.configureAsset.inReportingCurrency', {
                value: numberFormat(
                  formik.values.entryPriceInReportingCurrency
                ),
                currency: reportingCurrency,
              })}
            </div>
          )}
        </div>

        {/* DATE */}
        <div className="w-full">
          <DatePicker
            size="full"
            placeholder={t('strategies.custom.form.placeholder.date')}
            date={
              formik.values.purchasedAt
                ? new Date(formik.values.purchasedAt)
                : undefined
            }
            id={'date'}
            showLabel={true}
            fixedLabel={false}
            label={t('strategies.custom.form.label.date')}
            maxDate={new Date()}
            onChange={(date) => handlePurchasedAtChange(date as Date)}
            validate={(value) => !!value}
          />
        </div>
      </div>
      <div className="relative flex flex-col mt-10">
        <RevealButton
          className="mb-5 px-20"
          size="large"
          revealedLabel={t('strategies.configureAsset.advancedSettings')}
          notRevealedLabel={t('strategies.configureAsset.advancedSettings')}
          setIsMoreRevealed={setIsMoreRevealed}
        />
        {isMoreRevealed && (
          <div className="flex flex-col">
            <AssetAdvancedSettings
              className="px-20"
              formik={formik}
              reportingCurrency={reportingCurrency}
              assetCurrency={reportingCurrency}
              entryExchangePrice={1}
            />
          </div>
        )}
      </div>
    </>
  );
};
